import { useState } from 'react'
import { Phone, MessageCircle, Target, Trash2 } from 'lucide-react'
import { motion } from 'framer-motion'
import { Button } from '@/components/ui/Button'
import { Textarea } from '@/components/ui/Input'
import type { CloserLead } from '@/types'

interface CloserLeadDetailsInfoFormsProps {
  lead: CloserLead
  showRegForm: boolean
  setShowRegForm: (v: boolean) => void
  showDiscardForm: boolean
  setShowDiscardForm: (v: boolean) => void
  regCanal: string
  setRegCanal: (v: string) => void
  regResultado: string
  setRegResultado: (v: string) => void
  regObs: string
  setRegObs: (v: string) => void
  onRegisterSubmit?: () => void
  onConvertToDeal: (id: string) => void
  onDiscardLead?: (status: string, motivo: string) => void
}

export function CloserLeadDetailsInfoForms({
  lead,
  showRegForm,
  setShowRegForm,
  showDiscardForm,
  setShowDiscardForm,
  regCanal,
  setRegCanal,
  regResultado,
  setRegResultado,
  regObs,
  setRegObs,
  onRegisterSubmit,
  onConvertToDeal,
  onDiscardLead,
}: CloserLeadDetailsInfoFormsProps) {
  const [discardStatus, setDiscardStatus] = useState('PERDIDO')
  const [discardMotivo, setDiscardMotivo] = useState('')

  const handleRegister = () => {
    if (!regResultado || !onRegisterSubmit) return
    onRegisterSubmit()
    setShowRegForm(false)
    setRegResultado('')
    setRegObs('')
  }

  const handleDiscardSubmit = () => {
    if (!discardMotivo.trim()) return
    onDiscardLead?.(discardStatus, discardMotivo)
  }

  if (showRegForm) {
    return (
      <motion.div key='form' initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
        <div className='flex items-center justify-between mb-6'>
          <div className='flex items-center gap-3'>
            <div className='p-2 rounded-xl bg-accent/20 text-accent'><Phone size={20} /></div>
            <div>
              <h3 className='text-lg font-bold text-text-primary uppercase'>Registrar Interação</h3>
              <p className='text-[10px] text-text-muted uppercase tracking-wider'>Atualizar Histórico</p>
            </div>
          </div>
          <button onClick={() => setShowRegForm(false)} className='p-2 rounded-lg hover:bg-white/5 text-text-muted'>&times;</button>
        </div>
        <div className='space-y-4'>
          <div className='grid grid-cols-2 gap-4'>
            <div className='flex flex-col gap-1.5'>
              <label className='font-medium text-text-muted uppercase tracking-wider' style={{ fontSize: 'var(--font-size-label)' }}>Canal</label>
              <select value={regCanal} onChange={e => setRegCanal(e.target.value)} className='bg-[var(--glass-bg)] border border-white/[0.06] text-text-primary rounded-md px-3 py-2 text-[13px] outline-none focus:border-accent min-h-[38px]'>
                {['Telefone', 'WhatsApp', 'Email', 'Reunião', 'Outro'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className='flex flex-col gap-1.5'>
              <label className='font-medium text-text-muted uppercase tracking-wider' style={{ fontSize: 'var(--font-size-label)' }}>Resultado</label>
              <select value={regResultado} onChange={e => setRegResultado(e.target.value)} className='bg-[var(--glass-bg)] border border-white/[0.06] text-text-primary rounded-md px-3 py-2 text-[13px] outline-none focus:border-accent min-h-[38px]'>
                <option value=''>Selecione...</option>
                {['Sem Sucesso', 'Reunião Agendada', 'Follow-up', 'Perda de Interesse', 'Fechamento'].map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <Textarea label='Observações' value={regObs} onChange={e => setRegObs(e.target.value)} placeholder='Descreva o que foi conversado...' rows={3} />
          <Button variant='primary' className='w-full' onClick={handleRegister} disabled={!regResultado}>Registrar Interação</Button>
        </div>
      </motion.div>
    )
  }

  if (showDiscardForm) {
    return (
      <motion.div key='discard' initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
        <div className='flex items-center justify-between mb-6'>
          <div className='flex items-center gap-3'>
            <div className='p-2 rounded-xl bg-red-500/20 text-red-400'><Trash2 size={20} /></div>
            <div>
              <h3 className='text-lg font-bold text-text-primary uppercase'>Descartar Lead</h3>
              <p className='text-[10px] text-text-muted uppercase tracking-wider'>Informe o motivo para histórico</p>
            </div>
          </div>
          <button onClick={() => setShowDiscardForm(false)} className='p-2 rounded-lg hover:bg-white/5 text-text-muted'>&times;</button>
        </div>
        <div className='space-y-4'>
          <div className='flex flex-col gap-1.5'>
            <label className='font-medium text-text-muted uppercase tracking-wider' style={{ fontSize: 'var(--font-size-label)' }}>Motivo do Descarte</label>
            <select value={discardStatus} onChange={e => setDiscardStatus(e.target.value)} className='bg-[var(--glass-bg)] border border-white/[0.06] text-text-primary rounded-md px-3 py-2 text-[13px] outline-none focus:border-red-500/50 min-h-[38px]'>
              <option value="PERDIDO">Perdido (Sem interesse/Budget)</option>
              <option value="NUTRICAO">Nutrição (Em amadurecimento)</option>
              <option value="ERRO_CADASTRO">Erro de Cadastro</option>
              <option value="DEMISSAO_AMIGAVEL">Demissão Amigável</option>
            </select>
          </div>
          <Textarea label='Comentário Adicional' value={discardMotivo} onChange={e => setDiscardMotivo(e.target.value)} placeholder='Por que estamos perdendo este lead?' rows={3} />
          <Button variant='destructive' className='w-full' onClick={handleDiscardSubmit} disabled={!discardMotivo.trim()}>Confirmar Descarte</Button>
        </div>
      </motion.div>
    )
  }

  return (
    <>
      <div className='grid grid-cols-4 gap-3'>
        <button onClick={() => setShowRegForm(true)} className='group flex flex-col items-center justify-center gap-2 h-20 glass-card hover:bg-accent/10 transition-all rounded-xl'>
          <Phone size={24} className='text-text-primary group-hover:scale-110 transition-transform' />
          <span className='text-[9px] font-bold text-text-primary tracking-wider uppercase'>Registrar</span>
        </button>
        {lead.exclusividade ? null : (
          <button onClick={() => onConvertToDeal(lead.id)} className='group flex flex-col items-center justify-center gap-2 h-20 glass-card hover:bg-accent/10 transition-all rounded-xl'>
            <Target size={24} className='text-accent group-hover:scale-110 transition-transform' />
            <span className='text-[9px] font-bold text-text-primary tracking-wider uppercase'>Converter</span>
          </button>
        )}
        <button className='group flex flex-col items-center justify-center gap-2 h-20 glass-card hover:bg-emerald-500/10 transition-all rounded-xl'>
          <MessageCircle size={24} className='text-emerald-400 group-hover:scale-110 transition-transform' />
          <span className='text-[9px] font-bold text-text-primary tracking-wider uppercase'>WhatsApp</span>
        </button>
        <button onClick={() => setShowDiscardForm(true)} className='group flex flex-col items-center justify-center gap-2 h-20 glass-card hover:bg-red-500/10 transition-all rounded-xl'>
          <Trash2 size={24} className='text-red-400 group-hover:scale-110 transition-transform' />
          <span className='text-[9px] font-bold text-text-primary tracking-wider uppercase'>Descartar</span>
        </button>
      </div>
    </>
  )
}
